import React, {
  FC,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useIntl } from 'react-intl';

import { IStore } from '@rbi-ctg/store';
import useDialogModal from 'hooks/use-dialog-modal';
import useEffectOnUpdates from 'hooks/use-effect-on-updates';
import useMediaQuery, { MediaQuery } from 'hooks/use-media-query';
import { actions, useAppDispatch } from 'state/global-state';
import { removeAppliedRewardsInStorage } from 'state/global-state/models/loyalty/rewards/rewards.utils';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLocationContext } from 'state/location';
import { useLoyaltyContext } from 'state/loyalty';
import { useEvaluateLoyaltyAtRestaurant } from 'state/loyalty/hooks/use-evaluate-loyalty-at-restaurant';
import { ServiceMode, useServiceModeContext } from 'state/service-mode';
import { useStoreContext } from 'state/store';
import { toast } from 'utils/toast';

import { ICartEntryType, useInRestaurantCart } from './hooks/use-in-restaurant-redemption-cart';
import type { ICartEntry, ICreateCartEntryParams } from './hooks/use-in-restaurant-redemption-cart';
import { ShortCodeState, useShortCode } from './hooks/use-short-code';
import { ShortCodePollingStatus, useShortCodePollStatus } from './hooks/use-short-code-poll-status';
import { ShortCodeConfirmationModal, ShortCodeOverrideModal } from './modals';
import { IInRestaurantRedemptionContext, InRestaurantRedemptionFlagsState } from './types';
import { generateOrderLineItems } from './utils';

interface IOverrideModalConfirmation {
  overrideShortCodeCallback: () => void;
}

export const InRestaurantRedemptionContext = createContext<IInRestaurantRedemptionContext>(
  {} as IInRestaurantRedemptionContext
);
export const useInRestaurantRedemptionContext = () => useContext(InRestaurantRedemptionContext);

export const InRestaurantRedemptionProvider: FC = ({ children }) => {
  const { loyaltyUser, loyaltyLoading, refetchLoyaltyUser } = useLoyaltyContext();
  const { formatMessage } = useIntl();
  const { navigate } = useLocationContext();
  const { noStoreSelected, store } = useStoreContext();
  const { evaluateLoyaltyAtRestaurant } = useEvaluateLoyaltyAtRestaurant();
  const { isDelivery, serviceMode } = useServiceModeContext();
  const isMobile = useMediaQuery(MediaQuery.Mobile);
  const previousLoyaltyUserIdRef = useRef<string>();
  const loyaltyUserId = loyaltyUser?.id;
  const [ShortCodeOverrideModalConfirmation, openOverrideModalConfirmation] = useDialogModal<
    IOverrideModalConfirmation
  >({
    Component: ShortCodeOverrideModal,
    onConfirm: ({ overrideShortCodeCallback }: IOverrideModalConfirmation) => {
      overrideShortCodeCallback();
    },
  });

  const enableInRestaurantRedemption = Boolean(
    useFlag(LaunchDarklyFlag.ENABLE_IN_RESTAURANT_LOYALTY)
  );
  const inRestaurantRedemptionEnabled = Boolean(
    isMobile && enableInRestaurantRedemption && !isDelivery && (!!loyaltyUserId || loyaltyLoading)
  );

  const [
    { inRestaurantLoyaltyEnabledAtRestaurant, inRestaurantLoyaltyEnabledAtRestaurantLoading },
    setFlagsConfig,
  ] = useState<InRestaurantRedemptionFlagsState>({
    inRestaurantLoyaltyEnabledAtRestaurantLoading: true,
    inRestaurantLoyaltyEnabledAtRestaurant: inRestaurantRedemptionEnabled,
  });

  /** Evaluates InRestaurantRedemption feature availability against a store different from the one set in context */
  const isInRestaurantRedemptionEnabledAtStore = useCallback(
    async (storeToCheck: IStore, serviceModeToCheck: ServiceMode | null) => {
      if (!inRestaurantRedemptionEnabled) {
        return false;
      }

      return await evaluateLoyaltyAtRestaurant(storeToCheck, serviceModeToCheck);
    },
    [evaluateLoyaltyAtRestaurant, inRestaurantRedemptionEnabled]
  );

  /** Evaluates restaurant loyalty ability against the current Store in the context */
  useEffect(() => {
    let mounted = true;

    async function setEnabledAtRestaurant() {
      setFlagsConfig(prev =>
        prev.inRestaurantLoyaltyEnabledAtRestaurantLoading
          ? prev
          : { ...prev, inRestaurantLoyaltyEnabledAtRestaurantLoading: true }
      );
      let isEnabledAtRestaurant = false;

      if (store) {
        isEnabledAtRestaurant = await isInRestaurantRedemptionEnabledAtStore(
          store as IStore,
          serviceMode
        );
      }

      if (mounted) {
        setFlagsConfig({
          inRestaurantLoyaltyEnabledAtRestaurant: isEnabledAtRestaurant,
          inRestaurantLoyaltyEnabledAtRestaurantLoading: false,
        });
      }
    }

    setEnabledAtRestaurant();

    return () => {
      mounted = false;
    };
  }, [isInRestaurantRedemptionEnabledAtStore, serviceMode, store]);

  const inRestaurantRedemptionStatusPollIntervalMs = useFlag(
    LaunchDarklyFlag.LOYALTY_IN_RESTAURANT_STATUS_POLL_INTERVAL_MS
  );
  const dispatch = useAppDispatch();

  const {
    inRestaurantCart,
    addInRestaurantCartEntry,
    removeInRestaurantCartEntry: removeInRestaurantRedemption,
    updateInRestaurantCartEntryQuantity: updateInRestaurantRedemptionQuantity,
    resetInRestaurantCart,
    isInRestaurantCartEmpty,
    existEntryTypeInRestaurantCart,
    clearInRestaurantCartAllRewards,
  } = useInRestaurantCart();
  const {
    resetShortCode,
    getNewShortCode,
    shortCodeLoading,
    shortCodeState,
    setShortCodeClaimed,
    setShortCodeExpired,
    shortCode,
  } = useShortCode();

  const skipPolling: boolean =
    !inRestaurantRedemptionStatusPollIntervalMs ||
    shortCodeLoading ||
    shortCodeState !== ShortCodeState.Pending;

  const { pollingShortCodeStatus, resetPollingStatus } = useShortCodePollStatus({
    loyaltyId: loyaltyUserId,
    skipPolling,
    shortCode,
    pollInterval: inRestaurantRedemptionStatusPollIntervalMs,
  });

  const storeNumber = store?.number;

  const resetInRestaurantRedemption = useCallback(() => {
    // reset all the in-restaurant state
    resetPollingStatus();
    resetShortCode();
    resetInRestaurantCart();

    // reset loyalty state
    removeAppliedRewardsInStorage();
    dispatch(actions.loyalty.resetLoyaltyRewardsState(loyaltyUser?.points ?? 0));
  }, [dispatch, loyaltyUser, resetInRestaurantCart, resetPollingStatus, resetShortCode]);

  const generateShortCode = () => {
    if (loyaltyUserId && !noStoreSelected) {
      getNewShortCode({
        inRestaurantOrder: generateOrderLineItems(inRestaurantCart),
        loyaltyId: loyaltyUserId,
        restaurantId: storeNumber,
      });
    }
  };

  useEffectOnUpdates(() => {
    resetShortCode();
    // generate a new short code on every cart update
    generateShortCode();
  }, [inRestaurantCart]);

  useEffect(() => {
    if (pollingShortCodeStatus === ShortCodePollingStatus.Confirmed) {
      setShortCodeClaimed();
    } else if (pollingShortCodeStatus === ShortCodePollingStatus.NotFound) {
      // expiring the code if it's not found by the polling request
      setShortCodeExpired();
    }
  }, [pollingShortCodeStatus, setShortCodeClaimed, setShortCodeExpired]);

  useEffect(() => {
    previousLoyaltyUserIdRef.current = loyaltyUserId;
  }, [loyaltyUserId]);

  useEffect(() => {
    // clean cart if the user changes or the feature isn't enabled
    if (
      !loyaltyLoading &&
      !isInRestaurantCartEmpty &&
      (previousLoyaltyUserIdRef.current !== loyaltyUserId ||
        !inRestaurantRedemptionEnabled ||
        (!inRestaurantLoyaltyEnabledAtRestaurant && !inRestaurantLoyaltyEnabledAtRestaurantLoading))
    ) {
      resetInRestaurantRedemption();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    inRestaurantLoyaltyEnabledAtRestaurantLoading,
    loyaltyLoading,
    loyaltyUserId,
    inRestaurantRedemptionEnabled,
    inRestaurantLoyaltyEnabledAtRestaurant,
  ]);

  /**
   * Adds the redemption object to the cart and applies the reward if necessary,
   * and displays a toast if the action was successful, and redirects the user
   * to the in-restaurant redemption page.
   */
  const addInRestaurantRedemptionEntry = useCallback(
    (item: ICreateCartEntryParams) => {
      const cartEntry = addInRestaurantCartEntry(item);

      if (cartEntry) {
        let entityType = 'offer';

        if (cartEntry.type === ICartEntryType.REWARD) {
          entityType = 'reward';
          // apply the reward
          const { engineReward } = cartEntry.details;
          dispatch(
            actions.loyalty.applyReward({
              cartId: engineReward.id,
              rewardBenefitId: engineReward.rewardBenefitId,
            })
          );
        }

        toast.success(
          formatMessage(
            { id: 'addToInRestaurantRedemptionCartSuccess' },
            {
              type: formatMessage({
                id: entityType,
              }),
            }
          )
        );
      }
      // TODO: move this to the consumer
      navigate(formatMessage({ id: 'routes.redemptionInRestaurant' }));
    },
    [addInRestaurantCartEntry, dispatch, formatMessage, navigate]
  );

  /**
   * Removes the cart entry from the cart and remove the reward from `applied rewards` if necessary.
   */
  const removeInRestaurantRedemptionEntry = useCallback(
    (cartEntry: ICartEntry) => {
      removeInRestaurantRedemption(cartEntry);

      if (cartEntry.type === ICartEntryType.REWARD) {
        const { engineReward } = cartEntry.details;

        // remove the applied reward
        dispatch(
          actions.loyalty.removeAppliedReward({
            cartId: engineReward.id,
            rewardBenefitId: engineReward.rewardBenefitId,
          })
        );
      }
    },
    [dispatch, removeInRestaurantRedemption]
  );

  /**
   * Updates the cart entry quantity and apply or unapply the reward depending on the given and previous quantity.
   */
  const updateInRestaurantRedemptionEntryQuantity = useCallback(
    (cartEntry: ICartEntry, quantity: number) => {
      updateInRestaurantRedemptionQuantity(cartEntry, quantity);

      // apply or un-apply the reward as many times as necessary
      if (cartEntry.type === ICartEntryType.REWARD) {
        const {
          details: { engineReward },
          quantity: beforeQuantity,
        } = cartEntry;
        const { applyReward, unApplyReward } = actions.loyalty;
        const isDecrementAction = quantity - beforeQuantity < 0;
        const handleAction = isDecrementAction ? unApplyReward : applyReward;

        const { id: cartId, rewardBenefitId } = engineReward;
        for (let i = 0; i < Math.abs(quantity - beforeQuantity); i++) {
          dispatch(
            handleAction({
              rewardBenefitId,
              cartId,
              loyaltyUser,
            })
          );
        }
      }
    },
    [dispatch, loyaltyUser, updateInRestaurantRedemptionQuantity]
  );

  /**
   * The actions that have the ability to override the short code, should be wrapper by
   * this function in order to display a confirmation modal before the action is called.
   */
  const overrideShortCodeActionWrapper = useCallback(
    <T extends (...args: any) => void>(callback: T): ((...args: Parameters<T>) => void) => (
      ...args: Parameters<T>
    ) => {
      if (pollingShortCodeStatus === ShortCodePollingStatus.Processing) {
        // call to the callback to override the state
        openOverrideModalConfirmation({
          overrideShortCodeCallback: () => callback(...args),
        });
      } else {
        callback(...args);
      }
    },
    [openOverrideModalConfirmation, pollingShortCodeStatus]
  );

  return (
    <InRestaurantRedemptionContext.Provider
      value={{
        inRestaurantLoyaltyEnabledAtRestaurant,
        inRestaurantLoyaltyEnabledAtRestaurantLoading,
        inRestaurantRedemptionEnabled,
        generateShortCode,
        inRestaurantRedemptionCart: inRestaurantCart,
        // applying the override short code wrapper to catch up when the cart is changing
        // and display a confirmation modal
        addInRestaurantRedemptionEntry: overrideShortCodeActionWrapper<
          typeof addInRestaurantRedemptionEntry
        >(addInRestaurantRedemptionEntry),
        removeInRestaurantRedemptionEntry: overrideShortCodeActionWrapper<
          typeof removeInRestaurantRedemptionEntry
        >(removeInRestaurantRedemptionEntry),
        updateInRestaurantRedemptionEntryQuantity: overrideShortCodeActionWrapper<
          typeof updateInRestaurantRedemptionEntryQuantity
        >(updateInRestaurantRedemptionEntryQuantity),
        isInRestaurantRedemptionCartEmpty: isInRestaurantCartEmpty,
        resetInRestaurantRedemption,
        shortCodeLoading,
        shortCodeState,
        shortCode,
        existEntryTypeInRestaurantRedemptionCart: existEntryTypeInRestaurantCart,
        clearInRestaurantRedemptionAllRewards: clearInRestaurantCartAllRewards,
        isInRestaurantRedemptionEnabledAtStore,
      }}
    >
      {shortCodeState === ShortCodeState.Claimed && (
        <ShortCodeConfirmationModal
          onMount={() => {
            resetInRestaurantRedemption();

            // re-fetching user points
            refetchLoyaltyUser();
          }}
        />
      )}
      <ShortCodeOverrideModalConfirmation />

      {children}
    </InRestaurantRedemptionContext.Provider>
  );
};
